import React from 'react';
import { captureException } from '@/lib/sentry';

type ErrorBoundaryProps = {
  fallback?: React.ReactNode;
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to Sentry with React error context
    captureException(error, {
      errorBoundary: true,
      componentStack: errorInfo.componentStack,
      errorInfo
    });

    // Still log to console in development
    if (import.meta.env.MODE !== 'production') {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div role="alert" className="text-sm p-4 border rounded bg-destructive/10 text-destructive">
          Something went wrong. Please try again.
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;


